/* istanbul ignore file */

import React, { useEffect } from 'react';
import { batch, connect, ConnectedProps } from 'react-redux';
import { PreventSwipeNavigation } from '@noths/polaris-client-components';
import { getExperiments } from '@noths/polaris-client-experiments';
import { acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import { DeliveryZoneCode, parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import { grid, maxWidthContainer, Placement } from '@noths/polaris-client-ribbons-design-system';
import { useRichRelevance } from '@noths/polaris-client-rich-relevance';
import { visuallyHidden } from '@noths/polaris-client-styles';
import {
  actions as userConfigurationActions,
  getOrSetLoop54UserId,
  getOrSetRichRelevanceSessionId,
} from '@noths/polaris-client-user-configuration';
import { getProductIdFromProductCode } from '@noths/polaris-client-utils';
import { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { parse } from 'url';

import { ApiErrorGuardContainer } from 'src/components/molecules/ApiErrorGuard';
import { BreadcrumbContainer } from 'src/components/molecules/Breadcrumb';
import { StickySection } from 'src/components/molecules/StickySection';
import { DeliveryAreaContainer } from 'src/components/organisms/DeliveryArea';
import { LinkCarouselContainer } from 'src/components/organisms/LinkCarousel';
import { OverallRatingContainer } from 'src/components/organisms/OverallRating';
import { PartnerStorefrontContainer } from 'src/components/organisms/PartnerStorefront';
import { ProductDescriptionContainer } from 'src/components/organisms/ProductDescription';
import { ProductImagesCarouselContainer } from 'src/components/organisms/ProductImagesCarousel';
import {
  AddToBasketButtonContainer,
  MAIN_PAGE_CTA_ID,
  PRODUCT_REVIEWS_ID,
  ProductInfoContainer,
} from 'src/components/organisms/ProductInfo';
import { ProductReviewsContainer } from 'src/components/organisms/ProductReviews';
import { RecommendedProductsShelfContainer } from 'src/components/organisms/RecommendedProductsShelf';
import { ERROR_PRODUCT_NOT_FOUND } from 'src/constants/errors';
import { getBasketWithOptions } from 'src/redux/basket';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import {
  actions as deliveryZoneOptionsActions,
  requestDeliveryZoneOptions,
} from 'src/redux/deliveryZoneOptions';
import { trackLoop54Event } from 'src/redux/loop54';
import {
  actions as personalisationActions,
  updatePersonalisationOptionsFromBasketItem,
} from 'src/redux/personalisation';
import { requestProductDetails } from 'src/redux/product';
import { requestProductReviews } from 'src/redux/reviews';
import { requestSimilarProducts } from 'src/redux/richRelevance/thunks';
import { selectProductJSONLDSchema } from 'src/redux/selectors';
import { AppThunkDispatch, getStore } from 'src/redux/store';
import * as contentstackService from 'src/services/contentstack';
import * as styles from 'src/styles/productDetails.styles';
import { NextConfig, PageProps } from 'src/types';
import { isOutOfStock } from 'src/utils';

type ProductDetailsPageProps = PageProps & ConnectedProps<typeof connector>;

export const CONTENT_SECTION_ID = 'product-details__content';

const { publicRuntimeConfig, serverRuntimeConfig } = getConfig() as NextConfig;

const mapStateToProps = (state: ReduxApplicationState) => ({
  inventoryStatus: state.product.inventoryStatus,
  metaDescription: state.product.metaDescription,
  partnerName: state.product.partner.name,
  productJSONLDSchema: selectProductJSONLDSchema(state),
  productTitle: state.product.title,
  productCode: state.product.code,
  categoryHintId: state.product.breadcrumbCategoryId,
  richRelevanceSessionId: state.userConfiguration.richRelevanceSessionId,
  hideOutOfStockPlacement: state.richRelevance[Placement.OutOfStock].products === null,
  hideActiveProductsPlacement: state.richRelevance[Placement.ActiveProducts].products === null,
  hideRecentlyViewedPlacement: state.richRelevance[Placement.RecentlyViewed].products === null,
  pageLiveRegionText: state.accessibility.statusMessage,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
  getBasket: () => dispatch(getBasketWithOptions()),
  updateEditedItem: (editedItem: string) =>
    dispatch(personalisationActions.updateEditedItem(editedItem)),
  updatePersonalisationOptionsFromBasketItem: () =>
    dispatch(updatePersonalisationOptionsFromBasketItem()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

const ProductDetailsPage = ({
  inventoryStatus,
  metaDescription,
  canonicalUrl,
  partnerName,
  productCode,
  productJSONLDSchema,
  productTitle,
  categoryHintId,
  richRelevanceSessionId,
  hideOutOfStockPlacement,
  hideActiveProductsPlacement,
  hideRecentlyViewedPlacement,
  getBasket,
  updateEditedItem,
  updatePersonalisationOptionsFromBasketItem,
  pageLiveRegionText,
}: ProductDetailsPageProps) => {
  const { dispatch } = getStore();
  const placements = isOutOfStock(inventoryStatus)
    ? [Placement.OutOfStock, Placement.SameSeller]
    : [Placement.RecentlyViewed, Placement.ActiveProducts, Placement.SameSeller];

  const { query } = useRouter();
  useRichRelevance(
    {
      API_KEY: publicRuntimeConfig.RICH_RELEVANCE_API_KEY,
      endpoint: publicRuntimeConfig.endpoints.richRelevance,
      sessionId: richRelevanceSessionId,
    },
    async (fetchPlacement, clearPlacement) => {
      for (const placement of placements) {
        window.R3_COMMON.addPlacementType(placement);
      }
      window.R3_COMMON.addCategoryHintId(categoryHintId);
      window.R3_ITEM = new window.r3_item();
      window.R3_ITEM.setId(String(getProductIdFromProductCode(productCode)));
      window.R3_ITEM.setName(productTitle);
      window.RR.renderDynamicPlacements = null;

      await fetchPlacement();
      (window.RR?.data?.JSON?.placements ?? []).forEach((placements: RichRelevancePlacement) =>
        (dispatch as AppThunkDispatch)(requestSimilarProducts(placements)),
      );
      clearPlacement();
    },
  );

  const retrieveEditItem = async () => {
    updateEditedItem(query.editItem as string);
    await getBasket();
    updatePersonalisationOptionsFromBasketItem();
  };

  useEffect(() => {
    if (query.editItem) retrieveEditItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.editItem]);

  useEffect(() => {
    (dispatch as AppThunkDispatch)(requestDeliveryZoneOptions());
    (dispatch as AppThunkDispatch)(requestProductReviews({}));
    (dispatch as AppThunkDispatch)(trackLoop54Event({ eventType: 'click' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Head>
        <title>{`${productTitle} By ${partnerName}`} | notonthehighstreet.com</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(productJSONLDSchema) }}
        />
      </Head>
      <ApiErrorGuardContainer>
        <div css={visuallyHidden} role="status">
          {pageLiveRegionText}
        </div>
        {/* Note: the id and data attribute are used by the VM team to open PDP pages in the CMS */}
        <div css={styles.wrapper}>
          <div id="product" data-product-id={productCode}>
            <div css={[grid, maxWidthContainer]}>
              <div css={styles.breadcrumbContainer}>
                <BreadcrumbContainer />
              </div>
            </div>
            <PreventSwipeNavigation
              css={[grid, maxWidthContainer, styles.recommendedProductsContainer]}
              data-hide-placement={hideOutOfStockPlacement}
            >
              {isOutOfStock(inventoryStatus) && !hideOutOfStockPlacement && (
                <div css={styles.recommendationCarousel}>
                  <RecommendedProductsShelfContainer placementType={Placement.OutOfStock} />
                </div>
              )}
            </PreventSwipeNavigation>
            <div
              id={CONTENT_SECTION_ID}
              css={[grid, maxWidthContainer, styles.imagesAndInfoWrapper]}
            >
              <div css={styles.imagesContainer}>
                <ProductImagesCarouselContainer />
              </div>
              <div css={styles.productInfoContainer}>
                <ProductInfoContainer />
              </div>
            </div>
            <PreventSwipeNavigation css={[maxWidthContainer, styles.productDescriptionContainer]}>
              <ProductDescriptionContainer />
            </PreventSwipeNavigation>
            <div
              css={[grid, maxWidthContainer, styles.productReviewsOuterWrapper]}
              id={PRODUCT_REVIEWS_ID}
            >
              <div css={styles.productReviewsWrapper}>
                <ProductReviewsContainer />
              </div>
              <div css={styles.overallRatingWrapper}>
                <OverallRatingContainer />
              </div>
            </div>
          </div>
          <div css={[grid, maxWidthContainer]}>
            <DeliveryAreaContainer />
          </div>
          <PreventSwipeNavigation
            css={[
              grid,
              maxWidthContainer,
              styles.recommendedProductsContainer,
              styles.activeProductsContainer,
            ]}
            data-hide-placement={hideActiveProductsPlacement}
          >
            {!isOutOfStock(inventoryStatus) && !hideActiveProductsPlacement && (
              <div css={styles.recommendationCarousel}>
                <RecommendedProductsShelfContainer placementType={Placement.ActiveProducts} />
              </div>
            )}
          </PreventSwipeNavigation>
          <div css={styles.storefrontWrapper}>
            <PartnerStorefrontContainer />
          </div>
          <div css={[grid, maxWidthContainer]}>
            <div css={styles.linkCarouselWrapper}>
              <LinkCarouselContainer />
            </div>
          </div>
          <PreventSwipeNavigation
            css={[grid, maxWidthContainer, styles.recommendedProductsContainer]}
            data-hide-placement={hideRecentlyViewedPlacement}
          >
            {!isOutOfStock(inventoryStatus) && !hideRecentlyViewedPlacement && (
              <div css={styles.recommendationCarousel}>
                <RecommendedProductsShelfContainer placementType={Placement.RecentlyViewed} />
              </div>
            )}
          </PreventSwipeNavigation>
          <StickySection triggerElementId={MAIN_PAGE_CTA_ID}>
            <AddToBasketButtonContainer />
          </StickySection>
        </div>
      </ApiErrorGuardContainer>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ query, res, req }) => {
  const { ...queryParams } = query;

  const pathname = parse(req.url!).pathname!;
  const domain = publicRuntimeConfig.hosts.domain;
  const canonicalUrl = `https://${domain}${pathname}`;

  const { dispatch, getState } = getStore({
    router: { pathname, query: queryParams },
  });

  const {
    countryCode: initialCountryCode,
    currency: initialCurrency,
    deliveryZone: initialDeliveryZone,
  } = parseLocaleSetCookieHeaders(res.getHeaders());
  const { country_code, currency, delivery_zone, rr_session, Loop54User } = req.cookies;
  const functionalCookiesConsented = acceptsFunctionalCookies(req.cookies);
  const richRelevanceSessionId = getOrSetRichRelevanceSessionId(res, rr_session);
  const experiments = (req && getExperiments(req)) ?? '';

  batch(() => {
    dispatch(userConfigurationActions.setCountryCode(initialCountryCode ?? country_code));
    dispatch(userConfigurationActions.setCurrencyCode(initialCurrency ?? currency));
    dispatch(userConfigurationActions.setDeliveryZone(initialDeliveryZone ?? delivery_zone));
    dispatch(userConfigurationActions.setRichRelevanceSessionId(richRelevanceSessionId));
    dispatch(userConfigurationActions.setExperiments(experiments));
    dispatch(deliveryZoneOptionsActions.setSelectedCountry(country_code));
    dispatch(deliveryZoneOptionsActions.setSelectedZone(delivery_zone as DeliveryZoneCode));

    if (functionalCookiesConsented) {
      const loop54UserId = getOrSetLoop54UserId(res, Loop54User);
      dispatch(userConfigurationActions.setLoop54UserId(loop54UserId));
    }
  });

  const [, [navData, topBannerData]] = await Promise.all([
    (dispatch as AppThunkDispatch)(
      requestProductDetails({
        cookie: req.headers.cookie,
        query: {
          uri: pathname,
          previewing: queryParams.previewing,
        },
      }),
    ),
    contentstackService.fetchData(req, experiments),
  ]);

  const state: ReduxApplicationState = getState();
  const {
    deliveryZoneOptions,
    personalisation,
    product,
    productImagesCarousel,
    reviews,
    userConfiguration,
  } = state;

  if (product.redirectUrl) {
    return {
      redirect: {
        destination: product.redirectUrl,
        statusCode: 301,
      },
    };
  }

  if (product.error) {
    res.statusCode = product.error.name === ERROR_PRODUCT_NOT_FOUND ? 404 : 500;

    if (res.statusCode === 503) {
      res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);
    }
  }

  const props: PageProps = {
    canonicalUrl,
    navData,
    topBannerData,
    updatedReduxState: {
      // Cherry pick state to be updated on the client
      deliveryZoneOptions,
      personalisation,
      product,
      productImagesCarousel,
      reviews,
      userConfiguration,
    },
  };

  return { props };
};

export default connector(ProductDetailsPage);
